function selectedWaves(checkedWaves) {
  return Object.values(checkedWaves || {}).filter(
    wave => wave && typeof wave === 'object',
  );
}

function createdAtValue(wave) {
  const value = Date.parse(wave?.createdAt);
  return Number.isNaN(value) ? Number.POSITIVE_INFINITY : value;
}

function compareWaves(left, right) {
  const createdDifference = createdAtValue(left) - createdAtValue(right);
  if (createdDifference !== 0) return createdDifference;
  return String(left?._id || '').localeCompare(String(right?._id || ''));
}

function availableArrayTotal(waves, field) {
  if (!waves.every(wave => Array.isArray(wave?.[field]))) return null;
  return waves.reduce((total, wave) => total + wave[field].length, 0);
}

function pluralize(count, singular) {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

export function buildMergeConfirmation(checkedWaves) {
  const waves = selectedWaves(checkedWaves).sort(compareWaves);
  const [destinationWave, ...sourceWaves] = waves;
  const totalAllocations = availableArrayTotal(waves, 'allocations');
  const totalOrders = availableArrayTotal(waves, 'orders');
  const totals = [];

  if (totalAllocations !== null) totals.push(pluralize(totalAllocations, 'allocation'));
  if (totalOrders !== null) totals.push(pluralize(totalOrders, 'order'));

  const totalsCopy = totals.length > 0 ? ` (${totals.join(' across ')})` : '';
  const destinationLabel =
    destinationWave?.waveNumber || destinationWave?._id || 'unknown';
  const sourceWaveCount = sourceWaves.length;

  return {
    destinationWave,
    sourceWaves,
    sourceWaveCount,
    totalAllocations,
    totalOrders,
    message: `Merge ${pluralize(sourceWaveCount, 'source wave')}${totalsCopy} into destination wave ${destinationLabel}. The source waves will close after the merge.`,
  };
}

export function createIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return `wave-merge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createMergeConfirmationSession({
  checkedWaves,
  warehouseId,
  createIdempotencyKey: createKey = createIdempotencyKey,
}) {
  const confirmation = buildMergeConfirmation(checkedWaves);
  const idempotencyKey = createKey();

  return {
    ...confirmation,
    payload: {
      warehouseId,
      destinationWaveId: confirmation.destinationWave?._id,
      sourceWaveIds: confirmation.sourceWaves.map(wave => wave._id),
      // Temporary compatibility for dashboards deployed before the API rollout.
      waveIds: [
        confirmation.destinationWave?._id,
        ...confirmation.sourceWaves.map(wave => wave._id),
      ],
      idempotencyKey,
    },
  };
}

function mergeActorLabel(actor) {
  if (!actor) return null;
  if (typeof actor === 'string') {
    return /^[a-f\d]{24}$/i.test(actor) ? null : actor;
  }
  return actor.name || actor.fullName || actor.email || null;
}

export function getWaveMergeHistory(wave) {
  const metadata = wave?.metadata || {};
  const mergedFrom = wave?.mergedFrom ?? metadata.mergedFrom;
  return {
    createdAt: wave?.createdAt || null,
    lastMergedAt: wave?.lastMergedAt ?? metadata.lastMergedAt ?? null,
    lastMergedBy: mergeActorLabel(
      wave?.lastMergedByUserId ?? metadata.lastMergedByUserId,
    ),
    mergeVersion: wave?.mergeVersion ?? metadata.mergeVersion ?? null,
    mergedFromCount: Array.isArray(mergedFrom) ? mergedFrom.length : 0,
  };
}

export function getWaveHistoryRows(wave) {
  const history = getWaveMergeHistory(wave);
  const rows = [{ label: 'Created', timestamp: history.createdAt, detail: null }];

  if (history.lastMergedAt) {
    const details = [];
    if (history.lastMergedBy) details.push(`by ${history.lastMergedBy}`);
    if (history.mergeVersion !== null) details.push(`version ${history.mergeVersion}`);
    if (history.mergedFromCount > 0) {
      details.push(
        `${history.mergedFromCount} source${history.mergedFromCount === 1 ? '' : 's'}`,
      );
    }
    rows.push({
      label: 'Last merged',
      timestamp: history.lastMergedAt,
      detail: details.length > 0 ? details.join(' · ') : null,
    });
  }

  return rows;
}
